import { useState, useEffect } from 'react'
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  FileSignature,
  KeyRound,
  Layout,
  Lock,
  LogIn,
  Mail,
  Shield,
  Smartphone,
  X,
  XCircle,
} from 'lucide-react'

type AgreementSection = { heading: string; body: string[] }

type Props = {
  userName: string
  userEmail: string
  requireTotp: boolean
  requireAgreement: boolean
  requireNda: boolean
  onClose: () => void
}

const AGREEMENT_SECTIONS: AgreementSection[] = [
  {
    heading: 'Confidentiality',
    body: [
      'The trial participant will protect non-public information disclosed in connection with this trial using at least reasonable care and will use that information only to evaluate or operate the trial environment.',
      'Confidential information includes platform workflows, pricing, models, underwriting data, borrower data, investor information, integrations, screenshots, exports, and any other materials marked or reasonably understood to be confidential.',
    ],
  },
  {
    heading: 'Trial License Scope',
    body: [
      'The trial license is limited, non-transferable, revocable, and provided only for internal evaluation of Capital Markets OS for commercial real estate financing workflows.',
      'Trial access may be limited by user count, modules, data volume, environment restrictions, feature flags, and expiration dates defined by Helios FinTech.',
    ],
  },
  {
    heading: 'Usage Restrictions',
    body: [
      'The trial user may not reverse engineer, benchmark for publication, scrape, copy, mirror, or derive source code, models, prompts, workflows, schemas, or proprietary logic from the platform.',
    ],
  },
  {
    heading: 'Security, Monitoring, and Audit',
    body: [
      'The environment is monitored and audited. Login activity, IP address, timestamp, device metadata, workflow activity, and acceptance of this agreement may be recorded.',
    ],
  },
  {
    heading: 'Electronic Acceptance',
    body: [
      "Selecting 'Accept and Continue' constitutes the user's electronic signature and confirms authority to bind the applicable trial participant to these terms.",
    ],
  },
]

const NDA_SECTIONS: AgreementSection[] = [
  {
    heading: 'Purpose',
    body: [
      'This Non-Disclosure Agreement governs the exchange of confidential and proprietary information between Helios FinTech and the trial participant in connection with the evaluation of Capital Markets OS.',
    ],
  },
  {
    heading: 'Obligations of the Receiving Party',
    body: [
      'The receiving party shall hold all Confidential Information in strict confidence and shall not disclose it to any third party without the prior written consent of the disclosing party.',
    ],
  },
  {
    heading: 'Electronic Acceptance',
    body: [
      "Selecting 'Accept and Continue' constitutes the trial participant's electronic signature and confirms authority to be bound by the terms of this Non-Disclosure Agreement.",
    ],
  },
]

type StepDef = { label: string; icon: typeof Mail }

function StepIndicator({ steps, current }: { steps: StepDef[]; current: number }) {
  return (
    <div className="flex items-center justify-center gap-1">
      {steps.map((step, i) => {
        const Icon = step.icon
        const isActive = i === current
        const isDone = i < current
        return (
          <div key={`${step.label}-${i}`} className="flex items-center gap-1">
            {i > 0 && <div className={`h-px w-6 ${isDone ? 'bg-[#d9b768]' : 'bg-stone-700'}`} />}
            <div
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-medium transition-all ${
                isActive
                  ? 'border-[#d9b768]/40 bg-[#d9b768]/15 text-[#f6df9a]'
                  : isDone
                    ? 'border-[#d9b768]/25 bg-[#d9b768]/8 text-[#d9b768]'
                    : 'border-stone-700/50 bg-black/30 text-stone-500'
              }`}
            >
              {isDone ? <CheckCircle2 size={12} /> : <Icon size={12} />}
              <span>{step.label}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SectionCards({ title, sections }: { title: string; sections: AgreementSection[] }) {
  return (
    <div className="space-y-4">
      <div className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#d9b768]">{title}</div>
      {sections.map((section) => (
        <section key={section.heading} className="rounded-[20px] border border-amber-400/10 bg-black/20 px-4 py-3">
          <div className="flex items-center gap-2 text-[#f6df9a]">
            <FileSignature size={13} />
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.18em]">{section.heading}</h2>
          </div>
          <div className="mt-2 space-y-1.5">
            {section.body.map((p, idx) => (
              <p key={idx} className="text-[12px] leading-6 text-stone-300">{p}</p>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function buildSteps(requireTotp: boolean, requireAgreement: boolean, requireNda: boolean): StepDef[] {
  const steps: StepDef[] = [{ label: 'Login', icon: LogIn }]
  if (requireTotp) steps.push({ label: '2FA', icon: Smartphone })
  if (requireAgreement || requireNda) steps.push({ label: 'Agreement', icon: FileSignature })
  steps.push({ label: 'Dashboard', icon: Layout })
  return steps
}

export default function TrialExperiencePreview({
  userName,
  userEmail,
  requireTotp,
  requireAgreement,
  requireNda,
  onClose,
}: Props) {
  const [step, setStep] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [confirmA, setConfirmA] = useState(false)
  const [confirmB, setConfirmB] = useState(false)

  // Recompute steps whenever props change
  const allSteps = buildSteps(requireTotp, requireAgreement, requireNda)

  // Reset step to 0 whenever the configuration changes
  useEffect(() => {
    setStep(0)
    setConfirmA(false)
    setConfirmB(false)
  }, [requireTotp, requireAgreement, requireNda])

  // Clamp step in case steps shrink
  const safeStep = Math.min(step, allSteps.length - 1)
  const currentStepLabel = allSteps[safeStep]?.label ?? 'Login'
  const canNext = safeStep < allSteps.length - 1
  const canBack = safeStep > 0

  const displayName = userName || 'Trial User'
  const displayEmail = userEmail || 'user@example.com'

  // Summary of active gates for the config bar
  const gates: string[] = []
  if (requireTotp) gates.push('2FA')
  if (requireAgreement) gates.push('Agreement')
  if (requireNda) gates.push('NDA')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      {/* Dialog */}
      <div
        className="relative flex max-h-[90vh] w-full max-w-[900px] flex-col overflow-hidden rounded-2xl border border-amber-400/15 bg-[#0a0a0a] shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-400/10 px-5 py-3">
          <div className="flex items-center gap-3">
            <Eye size={15} className="text-[#d9b768]" />
            <span className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#d9b768]">Trial Experience Preview</span>
            <span className="rounded-full border border-stone-700 bg-black/50 px-2 py-0.5 text-[10px] text-stone-400">
              {displayName} &middot; {displayEmail}
            </span>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-stone-400 transition hover:bg-stone-800 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {/* ── Active configuration bar ──────────────────────────────── */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-400/8 bg-black/40 px-5 py-2">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-medium uppercase tracking-[0.2em] text-stone-500">Active Gates:</span>
            {gates.length > 0 ? (
              gates.map((g) => (
                <span key={g} className="rounded-full border border-amber-400/20 bg-amber-400/10 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                  {g}
                </span>
              ))
            ) : (
              <span className="text-[10px] text-stone-500 italic">None — direct login to dashboard</span>
            )}
          </div>
          <div className="text-[10px] text-stone-600">
            {allSteps.length} step{allSteps.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* ── Step indicator ─────────────────────────────────────── */}
        <div className="flex-shrink-0 border-b border-amber-400/8 bg-black/20 px-5 py-2.5">
          <StepIndicator steps={allSteps} current={safeStep} />
        </div>

        {/* ── Content ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {/* STEP: Login */}
          {currentStepLabel === 'Login' && (
            <div className="flex items-center justify-center px-4 py-10">
              <div className="login-dialog w-full max-w-[420px]">
                <div className="px-6 py-5 text-center">
                  <div className="flex justify-center">
                    <img alt="Helios FinTech" className="h-[52px] w-[160px] scale-[1.65] object-contain" src="/branding/helios-login-logo.svg" />
                  </div>
                  <div className="mt-3 text-[9px] font-medium uppercase tracking-[0.34em] text-[#d9b768]">Capital Markets OS</div>
                </div>
                <div className="px-6 pb-6">
                  <div className="login-panel">
                    <div className="space-y-3">
                      <div>
                        <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.24em] text-slate-400">Email</label>
                        <div className="relative">
                          <Mail size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
                          <input readOnly value={displayEmail} className="login-field" />
                        </div>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.24em] text-slate-400">Password</label>
                        <div className="relative">
                          <Lock size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
                          <input readOnly type={showPassword ? 'text' : 'password'} value="xR9kM2pL7qNw4vJb3c" className="login-field" />
                          <button className="absolute inset-y-0 right-3.5 flex items-center text-stone-500" onClick={() => setShowPassword(!showPassword)} type="button">
                            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        </div>
                      </div>
                      <div className="login-action pointer-events-none flex items-center justify-center gap-2">
                        <KeyRound size={15} /> Log in
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP: 2FA (only if requireTotp) */}
          {currentStepLabel === '2FA' && (
            <div className="flex items-center justify-center px-4 py-10">
              <div className="login-dialog w-full max-w-[420px]">
                <div className="px-6 py-5 text-center">
                  <div className="flex justify-center">
                    <img alt="Helios FinTech" className="h-[52px] w-[160px] scale-[1.65] object-contain" src="/branding/helios-login-logo.svg" />
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <div className="login-panel">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-300/10 text-amber-100">
                        <Smartphone size={20} />
                      </div>
                      <div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-slate-400">Two-Factor Auth</div>
                        <h2 className="mt-0.5 text-xl font-medium text-white">Enter verification code</h2>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.24em] text-slate-400">Authenticator Code</label>
                      <input readOnly value="483 291" className="login-field px-6 text-center text-xl tracking-[0.4em]" />
                    </div>
                    <div className="mt-3 login-action pointer-events-none flex items-center justify-center gap-2">
                      <Shield size={15} /> Verify & Log in
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP: Agreement (only if agreement or NDA required) */}
          {currentStepLabel === 'Agreement' && (
            <div className="px-4 py-5">
              <div className="mx-auto max-w-full">
                <div className="overflow-hidden rounded-xl border border-amber-400/10 bg-[#090909]">
                  <div className="border-b border-amber-400/10 px-5 py-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-[#d9b768]">Trial Access Agreement</div>
                        <h1 className="mt-1.5 text-lg font-medium text-white">
                          {requireAgreement && requireNda ? 'Trial License Agreement & NDA' : requireNda ? 'Non-Disclosure Agreement' : 'Trial License Agreement'}
                        </h1>
                        <p className="mt-1 max-w-2xl text-[11px] leading-5 text-stone-400">
                          Trial access is gated until the named license holder electronically accepts the terms below.
                        </p>
                      </div>
                      <div className="grid gap-2 sm:min-w-[240px]">
                        <div className="rounded-xl border border-amber-400/10 bg-black/25 px-3 py-2">
                          <div className="text-[9px] font-medium uppercase tracking-[0.22em] text-stone-500">License Holder</div>
                          <div className="mt-0.5 text-[12px] font-medium text-stone-100">{displayName}</div>
                          <div className="text-[11px] text-stone-400">{displayEmail}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-0 lg:grid-cols-[1.4fr_0.8fr]">
                    <div className="overflow-y-auto border-b border-amber-400/10 px-5 py-4 lg:border-b-0 lg:border-r">
                      {requireAgreement && <SectionCards title="Trial License Terms" sections={AGREEMENT_SECTIONS} />}
                      {requireNda && (
                        <div className={requireAgreement ? 'mt-6 border-t border-amber-400/10 pt-6' : ''}>
                          <SectionCards title="Non-Disclosure Agreement" sections={NDA_SECTIONS} />
                        </div>
                      )}
                    </div>

                    <div className="px-5 py-4">
                      <div className="login-panel">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-300/10 text-amber-100">
                            <Shield size={18} />
                          </div>
                          <div>
                            <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-[#d9b768]">Execution Controls</div>
                            <div className="mt-0.5 text-base font-medium text-white">Accept to Continue</div>
                          </div>
                        </div>

                        <div className="mt-3 space-y-2 text-[12px] text-stone-300">
                          <label className="flex items-start gap-2.5 rounded-xl border border-amber-400/10 bg-black/25 px-3 py-2">
                            <input checked={confirmA} className="mt-0.5 h-3.5 w-3.5 accent-amber-400" onChange={(e) => setConfirmA(e.target.checked)} type="checkbox" />
                            <span>I understand the environment is confidential, monitored, and restricted to approved trial use only.</span>
                          </label>
                          <label className="flex items-start gap-2.5 rounded-xl border border-amber-400/10 bg-black/25 px-3 py-2">
                            <input checked={confirmB} className="mt-0.5 h-3.5 w-3.5 accent-amber-400" onChange={(e) => setConfirmB(e.target.checked)} type="checkbox" />
                            <span>I have authority to electronically accept these terms on behalf of the trial participant.</span>
                          </label>
                        </div>

                        <div className="mt-3 rounded-xl border border-amber-400/10 bg-black/25 px-3 py-2.5 text-[11px] text-stone-400">
                          <div className="flex items-center gap-1.5 text-stone-200">
                            <Lock size={12} className="text-[#d9b768]" />
                            Electronic execution record
                          </div>
                          <div className="mt-1 leading-5">
                            Acceptance records the signer, timestamp, IP, and browser agent for audit.
                          </div>
                        </div>

                        <div className="mt-3 flex flex-col gap-2">
                          <div className={`login-action pointer-events-none flex items-center justify-center gap-2 ${!confirmA || !confirmB ? 'opacity-55' : ''}`}>
                            <CheckCircle2 size={15} /> Accept and Continue
                          </div>
                          <div className="btn-secondary pointer-events-none flex items-center justify-center gap-2 text-[11px]">
                            <XCircle size={13} /> Decline and Sign Out
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP: Dashboard (final) */}
          {currentStepLabel === 'Dashboard' && (
            <div className="flex items-center justify-center px-4 py-10">
              <div className="text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#d9b768]/30 bg-[#d9b768]/10">
                  <CheckCircle2 size={30} className="text-[#d9b768]" />
                </div>
                <h2 className="mt-4 text-xl font-medium text-white">Access Granted</h2>
                <p className="mt-1.5 max-w-md text-[12px] text-stone-400">
                  After completing all onboarding steps, <span className="text-stone-200">{displayName}</span> will land on the
                  Capital Markets OS Command Center.
                </p>
                <div className="mx-auto mt-5 max-w-sm rounded-xl border border-amber-400/10 bg-black/30 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-[0.24em] text-[#d9b768]">Onboarding Summary</div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-left text-[11px]">
                    <div className="rounded-lg border border-amber-400/8 bg-black/20 px-3 py-2">
                      <div className="text-stone-500">User</div>
                      <div className="mt-0.5 text-stone-200">{displayName}</div>
                    </div>
                    <div className="rounded-lg border border-amber-400/8 bg-black/20 px-3 py-2">
                      <div className="text-stone-500">Email</div>
                      <div className="mt-0.5 truncate text-stone-200">{displayEmail}</div>
                    </div>
                    <div className="rounded-lg border border-amber-400/8 bg-black/20 px-3 py-2">
                      <div className="text-stone-500">2FA</div>
                      <div className={`mt-0.5 ${requireTotp ? 'text-amber-300' : 'text-stone-500'}`}>{requireTotp ? 'Required' : 'Skipped'}</div>
                    </div>
                    <div className="rounded-lg border border-amber-400/8 bg-black/20 px-3 py-2">
                      <div className="text-stone-500">Agreements</div>
                      <div className={`mt-0.5 ${requireAgreement || requireNda ? 'text-amber-300' : 'text-stone-500'}`}>
                        {requireAgreement && requireNda ? 'Agreement + NDA' : requireAgreement ? 'Agreement Only' : requireNda ? 'NDA Only' : 'None'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer nav ──────────────────────────────────────────── */}
        <div className="flex flex-shrink-0 items-center justify-between border-t border-amber-400/10 bg-black/30 px-5 py-2.5">
          <button
            className={`btn-secondary flex items-center gap-1.5 text-[12px] ${!canBack ? 'invisible' : ''}`}
            disabled={!canBack}
            onClick={() => { setStep(safeStep - 1); setConfirmA(false); setConfirmB(false) }}
            type="button"
          >
            <ChevronLeft size={14} /> Back
          </button>
          <div className="text-[11px] text-stone-500">
            Step {safeStep + 1} of {allSteps.length}
          </div>
          {canNext ? (
            <button
              className="btn-primary flex items-center gap-1.5 text-[12px]"
              onClick={() => setStep(safeStep + 1)}
              type="button"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button className="btn-primary flex items-center gap-1.5 text-[12px]" onClick={onClose} type="button">
              Close Preview
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
